// CP2020 SP stacking — effective armor SP + BREACH THRESHOLDS per body location. The OTHER
// consumer of ArmorLayerSorter (EV is #lib/armor_ev). SP does NOT add: layered armor uses a
// proximity fold — the two-highest style bonus. Full rules + worked examples in
// docs/cp2020-armor-math.md.
//
// THE MODEL (settled with the user after long design work):
//   - Layers come in OUTER→INNER (ArmorLayerSorter.layersFor now returns that direction).
//   - effectiveSp GROWS the armor set outer→inner, one layer at a time, and folds each growing
//     SUBSET with the BOOK rule (the max+bonus proximity fold). The sequence of subset SPs is
//     the list of BREACH THRESHOLDS (outer→inner): threshold[k] = "the effective SP a hit has
//     faced by the time it reaches depth k" = fold of the k OUTERMOST layers. The LAST element
//     = the fold of the whole set = the complete effective SP (the book value — no inflation).
//   - This unifies effective-SP and breach: they are the SAME growing-subset walk, so they can
//     never disconnect, and it stays MONOTONIC (adding any layer never lowers protection — the
//     max() base means a weak outer layer, e.g. cover/a car, can't drag a strong inner layer
//     down; it only adds a diminishing bonus). See docs for the design rationale.
//   - effectiveSp is a STRATEGY DISPATCH (like #lib/damage): damageType selects how SP is
//     computed. `normal` is the plain fold; AP/DP/etc. (which transform layer SPs before
//     folding — halve each layer, etc.) come as separate strategies later.
//
// bonus(diff) — the proximity table, added to the higher SP, by |SP difference|:
//   0-4→+5, 5-8→+4, 9-14→+3, 15-20→+2, 21-26→+1, 27+→+0.
//
// Validity is loadout-wide (the sorter computes it once, over all locations): an illegal
// configuration makes the WHOLE readout invalid → { invalid: true }.

import { ArmorLayerSorter } from "#lib/armor_layers"

const LOCATIONS = ["head", "torso", "rarm", "larm", "rleg", "lleg"]

// Proximity bonus by |SP difference| — added to the higher of the two stacked values.
function bonus(diff) {
  const d = Math.abs(diff)
  if (d <= 4) return 5
  if (d <= 8) return 4
  if (d <= 14) return 3
  if (d <= 20) return 2
  if (d <= 26) return 1
  return 0
}

// The book fold: combine a set of layer SPs into one effective SP via max + proximity bonus,
// pairwise. Order-independent enough for its purpose (max is symmetric, bonus is on |diff|).
// 0 pieces → 0; a single piece → its own SP. `stack` takes layer objects (reads .sp).
function stack(layers) {
  if (layers.length === 0) return 0
  let total = Number(layers[0].sp) || 0
  for (let i = 1; i < layers.length; i++) {
    const sp = Number(layers[i].sp) || 0
    total = Math.max(total, sp) + bonus(total - sp)
  }
  return total
}

// --- strategies --------------------------------------------------------------
// A damageType's SP-side rule is a PER-LAYER TRANSFORM applied BEFORE folding: (layer, ctx) ->
// layer' with a possibly-modified .sp. It runs once per layer in true OUTER→INNER order, and is
// handed a CTX accumulator it may read AND mutate — so a rule can depend on the layers already
// passed, not just the current one. E.g. Blade/AP halves soft layers but STOPS at the first hard
// layer: it sets ctx.hitHard on that layer and, seeing ctx.hitHard, leaves inner layers full.
// `normal` is identity (ignores ctx). Transforms MUST NOT mutate the input layer (return a copy).
// Added here AND on the damage side together. The threshold WALK below is shared.
const spTransform = {
  normal: (layer) => layer,
}

// Grow the set outer→inner, fold each growing subset with the book rule; the subset SPs are the
// breach thresholds (outer→inner; last = complete effective SP). Two passes:
//   1. TRANSFORM: apply `xform` once per layer in outer→inner order, threading a fresh ctx {} so
//      a transform can carry state across layers (see spTransform). This yields the SP-adjusted
//      layer list. ctx accumulation runs over the TRUE outer→inner sequence, independent of (2).
//   2. FOLD: the growing-subset book fold over the transformed list.
// NOTE the two axes in (2): subsets GROW outer→inner (slice(0,k) = the k outermost), but each
// subset is FOLDED with the book rule which is INNER→OUTER. `stack` is order-sensitive for 3+
// layers, so we REVERSE the outer→inner slice back to inner→outer before folding — otherwise the
// final threshold would inflate above the book value (verified: scenario 3 = 24, not 26).
function walk(orderedLayers, xform) {
  const ctx = {}
  const transformed = orderedLayers.map((layer) => xform(layer, ctx)) // (1) outer→inner, carrying ctx
  const thresholds = []
  for (let k = 1; k <= transformed.length; k++) {
    const subset = transformed.slice(0, k).reverse() // (2) k outermost, back to inner→outer to fold
    thresholds.push(stack(subset))
  }
  return thresholds
}

// A strategy(orderedLayers) -> [breach thresholds]: the shared walk under this type's transform.
const STRATEGIES = { normal: (layers) => walk(layers, spTransform.normal) }

// --- public API --------------------------------------------------------------

// Effective SP + breach thresholds for ONE location's ORDERED (outer→inner) layer list.
// Dispatches to the damageType strategy (default normal; unknown falls back to normal).
// Returns the outer→inner threshold list: thresholds[k] = SP a hit has faced through depth k;
// LAST element = the complete effective SP. Empty layers → []. The caller holds the layer list
// and associates thresholds ↔ layers by index (for breach / degradation).
export function effectiveSp(orderedLayers, damageType = "normal") {
  const layers = Array.isArray(orderedLayers) ? orderedLayers : []
  const strategy = STRATEGIES[damageType] || STRATEGIES.normal
  return strategy(layers)
}

// pieces: the sorter's piece shape ({ sp, weight, isHard, isSkinWeave, isIntegral, isCover,
// locations }), already filtered to the ACTIVE set by the caller. Whole-body per-location
// effective SP for display (the show-sheet readout). Returns
//   { byLocation: { head, torso, … }, invalid: false }  normally, or
//   { byLocation: {}, invalid: true }                    for an illegal loadout.
export function computeSP(pieces) {
  const list = Array.isArray(pieces) ? pieces : []
  const sorter = new ArmorLayerSorter(list)
  if (!sorter.valid) return { byLocation: {}, invalid: true, invalidReason: sorter.invalidReason }

  const byLocation = {}
  for (const loc of LOCATIONS) {
    const thresholds = effectiveSp(sorter.layersFor(loc, "sp"))
    byLocation[loc] = thresholds.at(-1) ?? 0 // complete effective SP = last threshold
  }
  return { byLocation, invalid: false }
}

// Exported for tests / composition.
export { bonus, stack, walk, spTransform, STRATEGIES, LOCATIONS }
